import React, { useEffect, useContext, useState, useCallback } from "react"
import PropTypes from "prop-types"
import { AppContext } from "../components/context/AppContext"
import { OfferApi } from "../utils/api"
import { MaintenanceModeComponent, PageNotFound } from "../components"
import { AppleFitnessGetStartedComponent, AppleFitnessWelcomeComponent } from "../components/AppleFitness"
import AnalyticsService from "../utils/AnalyticsService"
import { ROUTE_PATHS } from "../utils/routes"
import { PROMO_CUSTOM_LANDING_PAGE_VIEW } from "../utils/GA4Events"

const BODY_TEXT_FIRST_SECTION = `<p>Lets start your Apple TV+ On Us submission</p> <p>Enter your T-Mobile for Business and select your promotion below based on your rate plan.&nbsp; You will then be prompted to log into your account.</p> <p>T-Mobile postpaid consumer voice customers ready to add Apple TV+,&nbsp;<u><strong><a href="https://www.t-mobile.com/account/benefit-redemption/paramountplus">click here</a></strong></u>&nbsp;to login and add your promotion.</p>`
const BODY_TEXT_FIRST_SECTION2 = `<p><strong>T-Mobile for Business customers, Business Unlimited Ultimate+, or equivalent plan</strong>&nbsp;<strong>can get Apple TV+ On Us!</strong>&nbsp;Initial code provides 12 months of Apple TV+ On Us. Cancel anytime. Subscriber account registration and credit card required. Before your 12-month code expires, if you are still on an eligible rate plan, you will be provided another code or given the option to continue the promotion on your T-Mobile bill. If you do not redeem the offer extension or cancel your subscription you will be billed $4.99/mo. when your 12-month promotional trial ends.&nbsp;</p> <p>&ndash;Unsure if your plan qualifies?&nbsp; Unlimited Premium, Unlimited Plus, Advantage Unlimited Plus, Business Unlimited Ultimate, Magenta Plus Business, and Magenta MAX for Business are also eligible, including Military, First Responder, 55+, and Deaf or Hard of Hearing/Visually Impaired versions of these plans.</p>`

export const AppleFitness = () => {
  const { dispatch, state } = useContext(AppContext)
  const [inMaintenanceMode, setInMaintenanceMode] = useState(true)
  const [maintenanceText, setMaintenanceText] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [isErrorFetchingMaintenanceText, setIsErrorFetchingMaintenanceText] = useState(false)
  const [landingPageFirstSection, setLandingPageFirstSection] = useState("")
  const [promoCode_1, setPromocode_1] = useState("")
  const [promoCode_2, setPromocode_2] = useState("")
  const [landingPageSecSection, setLandingPageSecSection] = useState("")
  const [isPageNotFound, setIsPageNotFound] = useState(false)
  const offerId = Number.parseInt(process.env.REACT_APP_APPLETVP3_OFFER_ID)

  useEffect(() => {
    AnalyticsService.sendUniquePageview(ROUTE_PATHS.APPLEFITNESS)
    AnalyticsService.sendGA4FunnelEvent(PROMO_CUSTOM_LANDING_PAGE_VIEW, {
      page_path: ROUTE_PATHS.APPLEFITNESS,
      partner_name: "Apple"
    });
    dispatch({ type: "RESETUSER" })
  }, [dispatch])

  // Efficient, single fetch function for all data
  const fetchAllData = useCallback(async (isMounted) => {
    setIsLoading(true)
    setErrorMessage("")
    setIsErrorFetchingMaintenanceText(false)
    try {
      // Fetch maintenance and page not found flags in parallel
      const [clientFlag, clientText, pageNotFound, offerFlag, offerText] = await Promise.all([
        OfferApi.getClientAsset("MaintenanceFlag"),
        OfferApi.getClientAsset("MaintenanceText"),
        OfferApi.getOfferAsset(offerId, "PAGE_NOT_FOUND"),
        OfferApi.getOfferAsset(offerId, "MaintenanceFlag"),
        OfferApi.getOfferAsset(offerId, "MaintenanceText")
      ])
      if (!isMounted) { return }
      setIsPageNotFound(pageNotFound)
      // Maintenance logic
      const clientMaint = clientFlag.notes?.toLowerCase() === "true"
      const offerMaint = offerFlag.notes?.toLowerCase() === "true"
      if (clientMaint || offerMaint) {
        setMaintenanceText(clientMaint ? clientText.notes : offerText.notes)
        setInMaintenanceMode(true)
        return
      }
      setInMaintenanceMode(false)
      // Only fetch landing page text if not in maintenance
      const [firstSectionText, secondSectionText, applePromocode_1, applePromocode_2] = await Promise.all([
        OfferApi.getClientAsset("APPLE_OFFER_TEXT_1"),
        OfferApi.getClientAsset("APPLE_OFFER_TEXT_2"),
        OfferApi.getClientAsset("APPLE_PROMO_CODE_1"),
        OfferApi.getClientAsset("APPLE_PROMO_CODE_2")
      ])
      if (!isMounted) { return }
      setLandingPageFirstSection(firstSectionText?.notes || BODY_TEXT_FIRST_SECTION)
      setLandingPageSecSection(secondSectionText?.notes || BODY_TEXT_FIRST_SECTION2)
      setPromocode_1(applePromocode_1?.notes || "")
      setPromocode_2(applePromocode_2?.notes || "")
    } catch (error) {
      if (isMounted) {
        if (error.message === "Unable to retrieve client asset") {
          setIsErrorFetchingMaintenanceText(true)
        }
        setErrorMessage("Something went wrong. Please try again.")
      }
    } finally {
      if (isMounted) { setIsLoading(false) }
    }
  }, [offerId])

  useEffect(() => {
    let isMounted = true
    fetchAllData(isMounted)
    return () => { isMounted = false }
  }, [fetchAllData])

  const ActiveContent = () => (
    <>
      {!isPageNotFound && (
        <AppleFitnessGetStartedComponent
          errorMessage={state.commonError}
          offerId={offerId}
          firstSectionText={landingPageFirstSection}
          secondSectionText={landingPageSecSection}
          setErrorMessage={setErrorMessage}
          cameosParkPromocode={promoCode_1}
          jazinFernoPromocode={promoCode_2}
        />
      )}
    </>
  )

  return (
    <>
      <AppleFitnessWelcomeComponent />
      {isPageNotFound && <PageNotFound />}
      {isErrorFetchingMaintenanceText && (
        <div className="text-center container">
          <h1 className="magenta">Temporarily Unavailable</h1>
          <p>This site is currently under going scheduled maintenance. We&apos;ll be back soon.</p>
          <p>Thank you for your patience.</p>
        </div>
      )}
      {isLoading && <div style={{ textAlign: "center" }}>Loading ...</div>}
      {errorMessage && !isLoading && !isErrorFetchingMaintenanceText && (
        <div style={{ textAlign: "center" }}>{errorMessage}</div>
      )}
      {inMaintenanceMode && !isLoading && <MaintenanceModeComponent maintenanceText={maintenanceText} />}
      {!inMaintenanceMode && !isLoading && !isErrorFetchingMaintenanceText && <ActiveContent />}
    </>
  )
}

AppleFitness.propTypes = {
  history: PropTypes.object
}
